import { Table } from 'antd';
import { ColumnType } from 'antd/es/table';
import { TablePaginationConfig } from 'antd/lib/table';
import { SortDirection } from 'Api/Features/General/Dtos/SortDirection';
import { UpdateTeamMembersActionDto } from 'Api/Features/Teams/Dtos/UpdateTeamMembersActionDto';
import { UpdateTeamMembersEntryRequestDto } from 'Api/Features/Teams/Dtos/UpdateTeamMembersEntryRequestDto';
import { UpdateTeamMembersRequestDto } from 'Api/Features/Teams/Dtos/UpdateTeamMembersRequestDto';
import { GetUsersRequestDto } from 'Api/Features/Users/Dtos/GetUsersRequestDto';
import { GetUsersSortColumnDto } from 'Api/Features/Users/Dtos/GetUsersSortColumnDto';
import { UserAccountStatusDto } from 'Api/Features/Users/Dtos/UserAccountStatusDto';
import { UserDto } from 'Api/Features/Users/Dtos/UserDto';
import Icon from 'Components/icons/Icon';
import Skeleton from 'Components/skeleton';
import TableFilters from 'Components/table-filters/table-filters';
import Tag, { TagColor } from 'Components/tag/tag';
import { TdWithImage } from 'Components/td-with-image';
import VerticalThreeDotMenuButton from 'Components/vertical-three-dot-menu-button';
import { useApiRequestHistory, useService, useStores } from 'Hooks';
import debounce from 'lodash.debounce';
import { autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
    DEBOUNCE_DELAY_400,
    SETTINGS_TEAMS_URL_REGEX,
    SETTINGS_URL,
    TEAMS_URL,
    TEAMS_URL_REGEX,
    USERS_URL,
} from 'Models/Constants';
import { AdvancedFilter } from 'Models/Filters/AdvancedFilter';
import { initialPaginationState } from 'Models/InitialPaginationState';
import { default as React, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { tableSkeletonShape } from 'Routes/settings/skeleton-shapes';
import { TeamService } from 'Services/TeamService';
import { UserService } from 'Services/UserService';
import { FilterStore } from 'Stores';
import { HistoryRequest, RequestType } from 'Stores/RequestHistoryStore';
import { theme } from 'Style/theme';
import { img, ImgSize } from 'Utils/ImgUtils';
import { getUserStatusTag } from 'Utils/TagUtils';
import { TeamDetailsContext } from '.';
import './coworkers-tab.less';
import { renderDefaultOfficeColumn } from 'Utils/WorkplaceUtils';

const getAdvancedFilters = (t: Function): AdvancedFilter[] => [
    {
        key: 'status',
        nameKey: 'Status',
        items: [
            {
                key: UserAccountStatusDto.Active,
                checked: true,
                displayNameKey: t(
                    `UserAccountStatusDto.UserAccountStatusDto_${UserAccountStatusDto.Active}`
                ),
            },
            {
                key: UserAccountStatusDto.Pending,
                checked: true,
                displayNameKey: t(
                    `UserAccountStatusDto.UserAccountStatusDto_${UserAccountStatusDto.Pending}`
                ),
            },
            {
                key: UserAccountStatusDto.Deactivated,
                checked: false,
                displayNameKey: t(
                    `UserAccountStatusDto.UserAccountStatusDto_${UserAccountStatusDto.Deactivated}`
                ),
            },
        ],
    },
];

interface CoworkersTabProps {
    interactionCausesFetch: boolean;
    onFetch: () => void;
    context: TeamDetailsContext;
}

const CoworkersTab: React.FunctionComponent<CoworkersTabProps> = observer(
    ({ interactionCausesFetch, onFetch, context }) => {
        const { id } = useParams<{ id: string }>();
        const userService = useService(UserService);
        const teamService = useService(TeamService);
        const paginationRef = useRef(initialPaginationState);
        const { t } = useTranslation();
        const advancedFilters = getAdvancedFilters(t);
        const filterStoreRef = useRef(new FilterStore({ advancedFilters }));
        const [pagination, setPagination] = useState<TablePaginationConfig>(initialPaginationState);
        const [loading, setLoading] = useState(false);
        const history = useHistory();
        const [coworkerList, setCoworkerList] = useState<UserDto[]>();
        const { toastStore, confirmationModalStore, globalLoadingStore, userStore, policyStore } =
            useStores();
        const [isLoading, setIsLoading] = useState({
            coworkers: false,
        });
        const setRequestHistory = useApiRequestHistory({
            useHistoryTrigger: {
                urlRegex:
                    context === TeamDetailsContext.Settings
                        ? SETTINGS_TEAMS_URL_REGEX
                        : TEAMS_URL_REGEX,
                requestType: RequestType.Users,
            },
            useHistoryCallback: (history: HistoryRequest) => {
                const filterStore = filterStoreRef.current;
                const requestFromStore: GetUsersRequestDto = history.request;
                const filtersList = [
                    { key: requestFromStore?.accountStatuses || [], parentKey: 'status' },
                ];
                filterStore.tickMultipleAdvancedFilter(filtersList);
                filterStore.searchTerm = requestFromStore.searchTerm ?? '';
                filterStore.teamIds = requestFromStore.teamIds ?? [];

                paginationRef.current = {
                    ...paginationRef.current,
                    current: requestFromStore.page ? requestFromStore.page + 1 : 1,
                    pageSize: requestFromStore.pageSize ?? initialPaginationState.pageSize,
                };
            },
        });

        const renderNameColumn = (coworker: UserDto) => {
            const tagProps = getUserStatusTag(t, coworker.accountStatus);
            return (
                <TdWithImage
                    defaultImg={<Icon iconName="PersonIcon" />}
                    imgSrc={coworker.imageUrl ? img(coworker.imageUrl, ImgSize.s) : undefined}
                >
                    <div className="user-infos">
                        <span className="title text-callout">
                            {coworker.firstName} {coworker.lastName}
                        </span>
                        <br />
                        <span className="subtitle text-caption">{coworker.contactInfo?.email}</span>
                    </div>
                    {coworker.accountStatus !== UserAccountStatusDto.Active && (
                        <Tag text={tagProps?.text ?? ''} color={tagProps?.color ?? TagColor.blue} />
                    )}
                </TdWithImage>
            );
        };

        const columns: ColumnType<UserDto>[] = [
            {
                title: t('name'),
                render: renderNameColumn,
                key: GetUsersSortColumnDto.Name,
                defaultSortOrder: 'ascend',
                sorter: true,
                onCell: (row: UserDto) => ({
                    onClick: (): void => {
                        onRowClick(row);
                    },
                }),
            },
            {
                title: t('Profile.job_title'),
                render: (coworker: UserDto) => coworker.jobTitle ?? t('n/a'),
                key: GetUsersSortColumnDto.JobTitle,
                sorter: true,
                onCell: (row: UserDto) => ({
                    onClick: (): void => {
                        onRowClick(row);
                    },
                }),
            },
            {
                title: t('default_office'),
                render: (user: UserDto) => renderDefaultOfficeColumn(user),
                key: GetUsersSortColumnDto.DefaultOffice,
                sorter: true,
                onCell: (row: UserDto) => ({
                    onClick: (): void => {
                        onRowClick(row);
                    },
                }),
            },
            {
                render: (row: UserDto) => (
                    <div className="t-align-center">
                        <VerticalThreeDotMenuButton
                            menuOptions={[
                                {
                                    title: t('remove_from_team'),
                                    className: 'secondary',
                                    onClick: (): Promise<void> => onRemoveCoworker(row.id ?? null),
                                },
                            ]}
                        />
                    </div>
                ),
                width: 100,
            },
        ];

        const getAdvancedFilterStatus = (): {
            checkedStatuses: UserAccountStatusDto[];
            statusesCount: number;
        } => {
            const filterStore = filterStoreRef.current;
            const statuses = filterStore.advancedFilters?.find(
                (filter: AdvancedFilter) => filter.key === 'status'
            );
            const checkedStatuses = statuses?.items
                .filter((item) => item.checked)
                .map((item) => {
                    return item.key;
                });

            return {
                checkedStatuses:
                    checkedStatuses?.map(
                        (x) => UserAccountStatusDto[x as keyof typeof UserAccountStatusDto]
                    ) ?? [],
                statusesCount: statuses?.items.length ?? 0,
            };
        };

        const fetchCoworkers = useCallback(
            async (params: {
                pagination: TablePaginationConfig;
                searchTerm?: string;
                advancedFilters?: AdvancedFilter[];
                sortColumn: GetUsersSortColumnDto | null;
                sortDirection: SortDirection | null;
            }) => {
                setLoading(true);

                try {
                    const statuses = getAdvancedFilterStatus();
                    if (statuses.checkedStatuses.length === 0) {
                        setCoworkerList([]);
                    } else {
                        const request: GetUsersRequestDto = setRequestHistory({
                            request: {
                                pageSize: params.pagination.pageSize || 0,
                                page: (params.pagination.current || 1) - 1,
                                searchTerm: params.searchTerm,
                                teamIds: [id],
                                accountStatuses: statuses.checkedStatuses,
                                sortColumn: params.sortColumn,
                                sortDirection: params.sortDirection,
                                managementRoles: [],
                            },
                            requestType: RequestType.Users,
                        });

                        const [items, totalItemsCount] = await userService.getUsers(request);
                        setIsLoading((prev) => {
                            return { ...prev, coworkers: true };
                        });
                        setCoworkerList(items);
                        setPagination({
                            ...params.pagination,
                            total: totalItemsCount,
                        });
                        onFetch();
                    }
                } catch (e: any) {
                    if (!e.treated) {
                        toastStore.genericError();
                    }
                } finally {
                    setLoading(false);
                }
            },
            [userService]
        );

        const handleTableChange = async (
            pagination: TablePaginationConfig,
            filter: any,
            sorter: any
        ): Promise<void> => {
            let sortDirection: SortDirection | null;
            switch (sorter.order) {
                case 'ascend':
                    sortDirection = SortDirection.Ascending;
                    break;
                case 'descend':
                    sortDirection = SortDirection.Descending;
                    break;
                default:
                    sortDirection = null;
                    break;
            }
            const { advancedFilters, searchTerm } = filterStoreRef.current;
            await fetchCoworkers({
                pagination,
                searchTerm,
                advancedFilters,
                sortColumn: sorter.columnKey,
                sortDirection: sortDirection,
            });

            paginationRef.current = pagination;
        };

        const debounceSearch = useRef(
            debounce((params: { searchTerm?: string; advancedFilters?: AdvancedFilter[] }) => {
                fetchCoworkers({
                    pagination: {
                        ...paginationRef.current,
                        current: 1,
                    },
                    searchTerm: params.searchTerm,
                    advancedFilters: params.advancedFilters,
                    sortColumn: null,
                    sortDirection: null,
                });
            }, DEBOUNCE_DELAY_400)
        );

        useEffect(() => {
            const disposer = autorun(() => {
                const filterStore = filterStoreRef.current;
                debounceSearch.current({
                    searchTerm: filterStore.searchTerm,
                    advancedFilters: filterStore.advancedFilters,
                });
            });

            return (): void => {
                disposer();
            };
        }, [debounceSearch]);

        useEffect(() => {
            if (interactionCausesFetch) {
                const filterStore = filterStoreRef.current;
                fetchCoworkers({
                    pagination: {
                        ...paginationRef.current,
                    },
                    searchTerm: filterStore.searchTerm,
                    advancedFilters: filterStore.advancedFilters,
                    sortColumn: null,
                    sortDirection: null,
                });
            }
        }, [interactionCausesFetch]);

        const onRowClick = (coworker: UserDto): void => {
            if (context === TeamDetailsContext.Settings)
                history.push(SETTINGS_URL + USERS_URL + `/${coworker.id}`);
            if (context === TeamDetailsContext.MyTeams)
                history.push(TEAMS_URL + `/${id}` + USERS_URL + `/${coworker.id}`);
        };

        const onRemoveCoworker = async (coworkerId: string | null): Promise<void> => {
            if (!coworkerId) return;

            try {
                if (
                    !(await confirmationModalStore.confirm({
                        icon: <Icon iconName="WarningIcon" fill={theme['warning-mid-contrast']} />,
                        title: t('Team.remove_coworker_from_team_title'),
                        message: t('Team.remove_coworker_from_team_message'),
                        positiveText: t('confirm'),
                        negativeText: t('cancel'),
                    }))
                )
                    return;
                globalLoadingStore.addLoading();
                const request: UpdateTeamMembersRequestDto = {
                    entries: [
                        {
                            userId: coworkerId,
                            action: UpdateTeamMembersActionDto.Remove,
                        } as UpdateTeamMembersEntryRequestDto,
                    ],
                };
                await teamService.updateTeamMembers(id, request);
                if (coworkerId === userStore.userInfo?.id) {
                    await userStore.setUserInfo(coworkerId);
                    await policyStore.setPolicyRestrictionsAndObjectivesStats();
                }
                const filterStore = filterStoreRef.current;
                fetchCoworkers({
                    pagination: {
                        ...paginationRef.current,
                    },
                    searchTerm: filterStore.searchTerm,
                    advancedFilters: filterStore.advancedFilters,
                    sortColumn: null,
                    sortDirection: null,
                });
                toastStore.toast({
                    type: 'success',
                    messageKey: t('Toast.entity_action_success_message', {
                        param1: t('coworker'),
                        param2: t('Entity.lowercase_action_removed'),
                    }),
                });
            } catch (e: any) {
                if (!e.treated) {
                    toastStore.genericError();
                }
            } finally {
                globalLoadingStore.removeLoading();
            }
        };

        return (
            <div className="CoworkersTab">
                <TableFilters
                    filterStore={filterStoreRef.current}
                    includeSearch
                    includeAdvancedFilters
                    isLoaded={isLoading.coworkers}
                />

                <Skeleton placeholder={tableSkeletonShape} isLoaded={isLoading.coworkers}>
                    <Table
                        className="table-action-rows table-striped-rows"
                        dataSource={coworkerList}
                        columns={columns}
                        loading={loading}
                        rowKey={(coworker): string => coworker.id ?? ''}
                        pagination={pagination}
                        onChange={handleTableChange}
                        //rows are all equal, no resizing
                        tableLayout={'fixed'}
                    />
                </Skeleton>
            </div>
        );
    }
);

export default CoworkersTab;
